import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { getCookie, setCookie } from '@/shared/lib/cookies'

// Mismo patrón que Safyro Finanzas/Contadores (src/theme/ThemeContext.tsx):
// sigue el sistema hasta que el usuario elige explícito con el toggle, y a
// partir de ahí queda fijo. En cookie, no localStorage: ESLint bloquea
// localStorage en todo el repo porque el token de sesión vive solo en
// memoria (ver shared/lib/cookies.js) — esta preferencia no es sensible,
// pero reusa el mismo mecanismo que ya usa el consentimiento de cookies.
const STORE = 'humania_theme'

function systemPrefersDark() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

function loadExplicit() {
  const v = getCookie(STORE)
  return v === 'light' || v === 'dark' ? v : null
}

function saveExplicit(v) {
  setCookie(STORE, v)
}

function apply(theme) {
  document.documentElement.dataset.theme = theme
}

const ThemeCtx = createContext(null)

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => loadExplicit() ?? (systemPrefersDark() ? 'dark' : 'light'))

  useEffect(() => {
    if (loadExplicit()) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setThemeState(mq.matches ? 'dark' : 'light')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    apply(theme)
  }, [theme])

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark'
      saveExplicit(next)
      return next
    })
  }, [])

  return <ThemeCtx.Provider value={{ theme, toggle }}>{children}</ThemeCtx.Provider>
}

export function useTheme() {
  const v = useContext(ThemeCtx)
  if (!v) throw new Error('useTheme debe usarse dentro de <ThemeProvider>')
  return v
}
